// src/components/education/CommunityLibraryBrowser.tsx
//
// Shared Community Course Library browser:
//   • EducationAdminPage — Create Class modal, Community Library tab
//   • ParentDashboardPage — Community Library card → modal
//   • /learn (StudentProgressPage) — empty-state door (parent: clone, student: read-only)
//
// LAW: never `.select('*')` from community_course_library — the view exposes
// module_plan (~40 KB/course). List = light columns only; the full plan is
// fetched per-course on expand.
//
// Origin badges: origin='master' → "Staff Pick" (platform-published),
// origin='teacher_created' → "Community" (teacher-authored).

import React, { useEffect, useRef, useState } from 'react'
import { BookOpen, Search, Loader2, Plus, Check, ChevronDown, X } from 'lucide-react'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { cloneCommunityCourse } from '@/utils/communityLibrary'

// Re-export so existing callers keep working without modifying import paths
export { cloneCommunityCourse }

// Canonical app tokens — verified identical in EducationAdminPage & StudentProgressPage.
const EDU = { sage: '#4a7c5e', sageDark: '#2d5a3d', navy: '#1a2740', cream: '#faf7f2', white: '#ffffff', muted: '#8a94a6', border: '#e2e8f0', blue: '#3b82f6' }
const btnGrad = `linear-gradient(135deg, ${EDU.sage}, ${EDU.sageDark})`

// Module-level plan cache — persists across modal open/close cycles.
// Key: community course id  Value: parsed ModuleSummary[]
// Rule: read surfaces cache; mutation surfaces don't.
const planCache = new Map<string, ModuleSummary[]>()

const LIST_COLUMNS = 'id, name, description, target_age, subject_group, origin, author_name, origin_facility_name, clone_count, module_count'

export interface CommunityCourseCard {
  id: string
  name: string
  description: string | null
  target_age: number | null
  subject_group: string | null
  origin: string
  author_name: string | null
  origin_facility_name: string | null
  clone_count: number
  module_count: number
}

interface ModuleSummary { title: string; lessons: number }

// ── Origin badge ──────────────────────────────────────────────────────────────
function OriginBadge({ origin }: { origin: string }) {
  const isStaff = origin === 'master'
  return (
    <span style={{
      fontSize: 9.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase',
      padding: '3px 8px', borderRadius: 999, flexShrink: 0,
      background: isStaff ? `${EDU.blue}50` : `${EDU.sageDark}2A`,
      color: isStaff ? EDU.blue : EDU.navy,
    }}>{isStaff ? 'Staff Pick' : 'Community'}</span>
  )
}

// ── Browser ───────────────────────────────────────────────────────────────────
export default function CommunityLibraryBrowser({
  canClone, onClone, readOnlyNote,
}: {
  canClone: boolean
  /** Implementations own error surfacing (toast). Throw on failure. */
  onClone?: (course: CommunityCourseCard, className: string) => Promise<void>
  readOnlyNote?: string
}) {
  const supabaseRef = useRef(createClientComponentClient())
  const supabase = supabaseRef.current
  const [courses, setCourses] = useState<CommunityCourseCard[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set())
  const [classNames, setClassNames] = useState<Record<string, string>>({})
  const [cloningId, setCloningId] = useState<string | null>(null)
  const [clonedIds, setClonedIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    supabase
      .from('community_course_library' as any)
      .select(LIST_COLUMNS)
      .order('clone_count', { ascending: false })
      .order('name', { ascending: true })
      .limit(100)
      .then(({ data, error }: any) => {
        if (error) setLoadError(error.message)
        else setCourses((data ?? []) as CommunityCourseCard[])
        setLoading(false)
      })
  }, [])

  const toggleExpand = async (id: string) => {
    if (expandedId === id) { setExpandedId(null); return }
    setExpandedId(id)
    // Cache hit → instant render, no fetch
    if (planCache.has(id)) return
    // Cache miss → fetch once, populate module-level cache
    setLoadingIds(prev => new Set(prev).add(id))
    const { data } = await supabase
      .from('community_course_library' as any)
      .select('module_plan')
      .eq('id', id)
      .maybeSingle()
    const plan = Array.isArray((data as any)?.module_plan) ? (data as any).module_plan : []
    planCache.set(id, plan.map((m: any) => ({
      title: String(m?.title ?? 'Module'),
      lessons: Array.isArray(m?.lessons) ? m.lessons.length : 0,
    })))
    setLoadingIds(prev => { const s = new Set(prev); s.delete(id); return s })
  }

  const handleClone = async (course: CommunityCourseCard) => {
    if (!onClone || cloningId) return
    setCloningId(course.id)
    try { await onClone(course, classNames[course.id]?.trim() || course.name) }
    finally { setCloningId(null) }
  }

  const filtered = courses.filter(c =>
    !search.trim() ||
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.subject_group?.toLowerCase().includes(search.toLowerCase()) ||
    c.author_name?.toLowerCase().includes(search.toLowerCase()))

  if (loading) return (
    <div style={{ padding: 32, textAlign: 'center', color: EDU.blue, fontSize: 13 }}>
      <Loader2 size={20} className="animate-spin" style={{ margin: '0 auto 8px', display: 'block' }} /> Loading library…
    </div>
  )
  if (loadError) return (
    <div style={{ padding: 20, textAlign: 'center', color: '#B91C1C', fontSize: 13 }}>
      Couldn't load the library: {loadError}
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <input type="text" placeholder="Search — organic gardening, veterinary basics, archery…"
        value={search} onChange={e => setSearch(e.target.value)}
        style={{ width: '100%', height: 44, padding: '0 14px', borderRadius: 12, border: `2px solid ${EDU.border}`,
          fontSize: 14, outline: 'none', boxSizing: 'border-box', background: EDU.white, color: EDU.navy }} />

      {courses.length > 0 && (
        <div style={{ fontSize: 11.5, color: EDU.blue }}>
          {courses.length} course{courses.length === 1 ? '' : 's'} · free to add · published by teachers, for teachers
        </div>
      )}

      {courses.length === 0 ? (
        <div style={{ padding: '32px 20px', textAlign: 'center', background: EDU.cream, borderRadius: 12, border: `2px dashed ${EDU.border}` }}>
          <BookOpen size={28} style={{ margin: '0 auto 10px', display: 'block', color: EDU.blue }} />
          <p style={{ color: EDU.blue, fontSize: 13, margin: 0, lineHeight: 1.5 }}>
            No community courses published yet. Generate a custom course and publish it from its Settings tab — it appears here for every educator.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: EDU.muted, fontSize: 13 }}>No courses match "{search}".</div>
      ) : (
        <div style={{ maxHeight: 360, overflowY: 'auto', border: `2px solid ${EDU.border}`, borderRadius: 12, background: EDU.white }}>
          {filtered.map(course => (
            <div key={course.id} style={{ borderBottom: `1px solid ${EDU.border}` }}>
              <button type="button" onClick={() => toggleExpand(course.id)}
                style={{ width: '100%', padding: '12px 14px', display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', gap: 10, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: EDU.navy, whiteSpace: 'nowrap',
                      overflow: 'hidden', textOverflow: 'ellipsis' }}>{course.name}</span>
                    <OriginBadge origin={course.origin} />
                  </div>
                  <div style={{ fontSize: 10.5, color: EDU.blue, marginTop: 3 }}>
                    by {course.author_name ?? 'Unknown'}
                    {course.origin_facility_name && course.origin_facility_name !== course.author_name ? ` · ${course.origin_facility_name}` : ''}
                    {' · '}{course.module_count} modules ·{' '}
                    {course.clone_count > 0
                      ? `${course.clone_count} ${course.clone_count === 1 ? 'classroom' : 'classrooms'} using it`
                      : 'new'}
                  </div>
                </div>
                {course.target_age ? <span style={{ fontSize: 10, color: EDU.muted, flexShrink: 0 }}>Age {course.target_age}+</span> : null}
                <ChevronDown size={16} color={EDU.muted}
                  style={{ flexShrink: 0, transform: expandedId === course.id ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
              </button>

              {expandedId === course.id && (
                <div style={{ padding: '2px 14px 16px' }}>
                  {course.description && (
                    <p style={{ fontSize: 12.5, color: EDU.muted, lineHeight: 1.55, margin: '0 0 12px' }}>{course.description}</p>
                  )}
                  {loadingIds.has(course.id) ? (
                    <div style={{ fontSize: 12, color: EDU.muted, padding: '8px 0' }}>
                      <Loader2 size={14} className="animate-spin" style={{ display: 'inline-block', marginRight: 6, verticalAlign: -2 }} /> Loading syllabus…
                    </div>
                  ) : (planCache.get(course.id) ?? []).length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 14 }}>
                      {planCache.get(course.id)!.map((m, i) => (
                        <div key={i} style={{ fontSize: 11.5, color: EDU.navy, display: 'flex', gap: 8 }}>
                          <span style={{ color: EDU.blue, fontWeight: 800 }}>{String(i + 1).padStart(2, '0')}</span>
                          <span style={{ flex: 1 }}>{m.title}</span>
                          <span style={{ color: EDU.blue }}>{m.lessons} lessons</span>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {canClone ? (
                    clonedIds.has(course.id) ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10,
                        background: `${EDU.sage}18`, color: EDU.sage, fontWeight: 700, fontSize: 13 }}>
                        <Check size={16} /> Added to your classes — assign it from your Subjects page
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input type="text" value={classNames[course.id] ?? course.name}
                          onChange={e => setClassNames(prev => ({ ...prev, [course.id]: e.target.value }))}
                          placeholder="Your class name"
                          style={{ flex: 1, height: 42, padding: '0 12px', borderRadius: 10, border: `2px solid ${EDU.border}`,
                            fontSize: 13.5, outline: 'none', boxSizing: 'border-box', background: EDU.white, color: EDU.navy }} />
                        <button onClick={() => handleClone(course)} disabled={cloningId !== null}
                          style={{ padding: '0 18px', borderRadius: 10, border: 'none', cursor: 'pointer', background: btnGrad,
                            color: '#fff', fontWeight: 700, fontSize: 13, opacity: cloningId !== null ? 0.6 : 1,
                            display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          {cloningId === course.id ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                          Add — Free
                        </button>
                      </div>
                    )
                  ) : readOnlyNote ? (
                    <p style={{ fontSize: 11.5, color: EDU.muted, margin: 0, fontStyle: 'italic' }}>{readOnlyNote}</p>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Modal wrapper — so each surface is a button + one JSX block ──────────────
export function CommunityLibraryModal({ open, onClose, canClone, onClone, readOnlyNote, title = 'Community Course Library' }: {
  open: boolean
  onClose: () => void
  canClone: boolean
  onClone?: (course: CommunityCourseCard, className: string) => Promise<void>
  readOnlyNote?: string
  title?: string
}) {
  if (!open) return null
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(20,26,38,0.55)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: EDU.white, borderRadius: 16, width: '100%', maxWidth: 620, maxHeight: '84vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(20,26,38,0.3)' }}>
        <div style={{ padding: '18px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: EDU.navy }}>{title}</h3>
            <p style={{ margin: '4px 0 0', fontSize: 11.5, color: EDU.muted }}>
              Clone any course free — it becomes yours to edit and assign. The original is untouched.
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: EDU.muted, padding: 4 }}>
            <X size={20} />
          </button>
        </div>
        <div style={{ padding: '0 20px 20px', overflowY: 'auto' }}>
          <CommunityLibraryBrowser canClone={canClone} onClone={onClone} readOnlyNote={readOnlyNote} />
        </div>
      </div>
    </div>
  )
}